import React from 'react';

export const MarketInsights: React.FC = () => {
  const insights = [
    {
      title: 'Suburban Migration Trends',
      description: 'The ongoing shift toward suburban living has increased demand for garden apartments. Remote work flexibility and desire for more space have made suburban garden communities increasingly attractive to renters.',
      impact: 'Positive for garden apartment values and occupancy rates'
    },
    {
      title: 'Affordability Advantage',
      description: 'Garden apartments typically offer lower rents than urban high-rises while providing more space. This affordability advantage positions garden apartments well in markets with housing cost pressures.',
      impact: 'Strong tenant demand across income levels'
    },
    {
      title: 'Land Value Component',
      description: 'Garden apartments sit on larger land parcels than high-density properties. In growing markets, this land can have significant redevelopment value, providing downside protection for investors.',
      impact: 'Additional value beyond current income stream'
    },
    {
      title: 'Operating Expense Trends',
      description: 'Insurance, property taxes, and maintenance costs have increased significantly. Garden apartments with deferred maintenance face particularly steep expense increases when brought to market standards.',
      impact: 'Pressure on net operating income for aging properties'
    },
    {
      title: 'Capital Expenditure Cycles',
      description: 'Garden apartments built in the 1970s-1990s are reaching major capital expenditure cycles. Roofs, parking lots, building systems, and unit interiors often need replacement simultaneously.',
      impact: 'Significant capital requirements for older properties'
    },
    {
      title: 'Competition from New Construction',
      description: 'New garden apartment communities with modern amenities and efficient layouts compete with older properties. Owners of aging properties must invest in renovations or accept lower rents.',
      impact: 'Renovation pressure on older garden apartments'
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Garden Apartment Market Insights
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            Understanding current market dynamics helps garden apartment owners make informed decisions about when and how to sell.
          </p>
        </div>

        <div className="mb-16">
          <div className="relative overflow-hidden rounded-2xl shadow-2xl">
            <img
              src="https://public.readdy.ai/ai/img_res/b454df9dab5b20654bb3e3c36ae3c0be.jpg"
              alt="Real Estate Market Analytics"
              className="w-full h-[450px] object-cover object-top"
            />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {insights.map((insight, index) => (
            <div
              key={index}
              className="bg-white rounded-xl shadow-lg p-8 border border-gray-200 hover:shadow-xl transition-all duration-300"
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                {insight.title}
              </h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                {insight.description}
              </p>
              <div className="pt-4 border-t border-gray-200">
                <p className="text-sm font-semibold text-orange-600 uppercase tracking-wide mb-2">
                  Market Impact
                </p>
                <p className="text-gray-700 leading-relaxed">
                  {insight.impact}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl p-10 text-white">
          <h3 className="text-3xl font-bold mb-6">
            What This Means for Garden Apartment Owners
          </h3>
          <div className="prose prose-lg max-w-none text-white">
            <p className="text-lg leading-relaxed mb-4 text-white/95">
              The garden apartment market presents both opportunities and challenges for current owners. Properties in strong suburban markets with stable tenant bases can command premium prices, especially if they've been well-maintained and updated. However, owners facing significant capital expenditure needs or operating in markets with substantial new competition may find that selling now makes more financial sense than investing heavily in renovations.
            </p>
            <p className="text-lg leading-relaxed mb-4 text-white/95">
              Timing matters in real estate. Interest rate environments, local market conditions, and property-specific factors all influence optimal selling decisions. We help garden apartment owners evaluate their specific situations and determine whether selling makes sense given current market conditions.
            </p>
            <p className="text-lg leading-relaxed text-white/95">
              Our experience across diverse markets and property conditions means we can provide realistic assessments of value and help you understand your options. Whether market conditions favor selling or suggest holding, we'll give you honest feedback based on our extensive garden apartment experience.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};